import { Card, Col, Descriptions, Row, Statistic, Tag } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { PublicAuditTrailSummary } from '../../types/audit-trail.types';
import { SurveyStatus } from '../../types/survey.types';
import { RichTextDisplay } from '../common/RichTextDisplay';

interface PublicAuditTrailHeaderProps {
  summary: PublicAuditTrailSummary;
}

const statusColorMap: Record<SurveyStatus, string> = {
  [SurveyStatus.DRAFT]: 'default',
  [SurveyStatus.SCHEDULED]: 'blue',
  [SurveyStatus.ACTIVE]: 'green',
  [SurveyStatus.PAUSED]: 'orange',
  [SurveyStatus.COMPLETED]: 'purple',
  [SurveyStatus.CANCELED]: 'red',
};

const statusLabelMap: Record<SurveyStatus, string> = {
  [SurveyStatus.DRAFT]: 'Rascunho',
  [SurveyStatus.SCHEDULED]: 'Agendada',
  [SurveyStatus.ACTIVE]: 'Ativa',
  [SurveyStatus.PAUSED]: 'Pausada',
  [SurveyStatus.COMPLETED]: 'Concluída',
  [SurveyStatus.CANCELED]: 'Cancelada',
};

const scopeLabelMap: Record<string, string> = {
  NATIONAL: 'Nacional',
  STATE: 'Estadual',
  CITY: 'Municipal',
};

const formatDateTime = (value: string) => new Date(value).toLocaleString('pt-BR');

export default function PublicAuditTrailHeader({ summary }: PublicAuditTrailHeaderProps) {
  const scopeDetailsParts = [
    summary.header.scopeDetails.country ? `País: ${summary.header.scopeDetails.country}` : null,
    summary.header.scopeDetails.state ? `Estado: ${summary.header.scopeDetails.state}` : null,
    summary.header.scopeDetails.city ? `Cidade: ${summary.header.scopeDetails.city}` : null,
  ].filter(Boolean) as string[];

  const scopeDetailsLabel = scopeDetailsParts.length > 0 ? scopeDetailsParts.join(' | ') : '-';

  return (
    <Card style={{ marginBottom: 16 }}>
      <Row gutter={[24, 16]} align="top">
        <Col xs={24} lg={18}>
          <Descriptions
            column={{ xs: 1, sm: 2, lg: 3 }}
            title="Dados Gerais da Pesquisa"
            size="small"
          >
            <Descriptions.Item label="Nome" span={2}>{summary.header.name}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColorMap[summary.header.status]}>{statusLabelMap[summary.header.status]}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Descrição" span={3}>
              <RichTextDisplay html={summary.header.description} />
            </Descriptions.Item>
            <Descriptions.Item label="Período de Execução" span={2}>
              {formatDateTime(summary.header.executionPeriod.startDate)} –{' '}
              {formatDateTime(summary.header.executionPeriod.endDate)}
            </Descriptions.Item>
            <Descriptions.Item label="Abrangência">
              {scopeLabelMap[summary.header.scope] || summary.header.scope}
              {scopeDetailsLabel !== '-' && ` · ${scopeDetailsLabel}`}
            </Descriptions.Item>
            <Descriptions.Item label="Visibilidade dos Resultados" span={3}>
              {summary.header.visibility?.disclosureRuleText ?? 'Resultado ainda não liberado'}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} lg={6} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', paddingTop: 28 }}>
          <Statistic
            title="Respostas coletadas"
            value={summary.metrics.responseCount}
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#2E2B81', fontSize: 28 }}
          />
        </Col>
      </Row>
    </Card>
  );
}
